import React from 'react';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { scheduleDayTitle } from '../../sampling/surveyScheduleCalendar';
import type { QuotationScheduleDaySummary } from './quotationScheduleCalendarUtils';

type Props = {
  open: boolean;
  date: string;
  summary: QuotationScheduleDaySummary | null;
  onClose: () => void;
  canCreateWorkOrder?: boolean;
  onCreateWorkOrder?: () => void;
  canEditSchedule?: boolean;
  onEditSchedule?: () => void;
  actionLabel?: string;
  onAction?: () => void;
  actionDisabled?: boolean;
  actionDisabledHint?: string;
  actionTestId?: string;
  onRefresh?: () => void;
  refreshing?: boolean;
  /** Buka halaman detail jadwal yang sedang ditampilkan di modal. */
  onOpenSchedule?: () => void;
};

export function resolveScheduleTahapanCaption(scheduleStatus: string, canEditSchedule: boolean): string | null {
  if (scheduleStatus === 'work_order_created') {
    return canEditSchedule
      ? 'Work order sudah dibuat. Admin sampling masih dapat menugaskan petugas dan mengatur tanggal.'
      : 'Work order sudah dibuat untuk penjadwalan ini.';
  }
  return null;
}

export function QuotationScheduleDayDetailDialog({
  open,
  date,
  summary,
  onClose,
  canCreateWorkOrder = false,
  onCreateWorkOrder,
  canEditSchedule = false,
  onEditSchedule,
  actionLabel,
  onAction,
  actionDisabled = false,
  actionDisabledHint,
  actionTestId = 'schedule-custom-action',
  onRefresh,
  refreshing = false,
  onOpenSchedule,
}: Props) {
  if (!summary) return null;

  const tahapanCaption = resolveScheduleTahapanCaption(summary.scheduleStatus, canEditSchedule);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      data-testid="schedule-quotation-day-detail-dialog"
    >
      <DialogTitle sx={{ position: 'relative', pr: onRefresh ? 6 : undefined }}>
        Detail penjadwalan
        {onRefresh ? (
          <Tooltip title="Muat ulang data penjadwalan">
            <span>
              <IconButton
                aria-label="Muat ulang data penjadwalan"
                onClick={onRefresh}
                disabled={refreshing}
                sx={{ position: 'absolute', right: 8, top: 8 }}
                data-testid="schedule-quotation-day-detail-refresh"
              >
                {refreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
              </IconButton>
            </span>
          </Tooltip>
        ) : null}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            {scheduleDayTitle(date)}
          </Typography>
          <Stack spacing={0.5}>
            <Typography variant="subtitle2">{summary.quotationNumber}</Typography>
            {summary.groupLabel ? (
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Group sample: {summary.groupLabel}
              </Typography>
            ) : null}
            <Typography variant="body2">{summary.customerName || '—'}</Typography>
          </Stack>

          <Stack spacing={0.5}>
            <Typography variant="subtitle2">Work order</Typography>
            <Typography variant="body2" data-testid="schedule-day-detail-job-number">
              Nomor job:{' '}
              {String(summary.workOrderJobNumber ?? '').trim() || 'Belum terbit'}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              data-testid="schedule-day-detail-work-order"
            >
              Work order:{' '}
              {String(summary.workOrderDocumentId ?? '').trim() || 'Belum dibuat'}
            </Typography>
          </Stack>

          <Stack spacing={0.5}>
            <Typography variant="subtitle2">Tim survey</Typography>
            <Typography variant="body2" color="text.secondary">
              Lead: {summary.teamLeadName} · Total: {summary.teamCount} petugas
            </Typography>
            <List dense disablePadding>
              {summary.members.map((member) => (
                <ListItem key={member.userId} sx={{ px: 0 }}>
                  <ListItemText
                    primary={`${member.name}${member.isLead ? ' (Lead)' : ''}`}
                    secondary={member.timeLabels.join(', ') || 'Sepanjang hari'}
                  />
                </ListItem>
              ))}
            </List>
          </Stack>

          <Stack spacing={0.5}>
            <Typography variant="subtitle2">Alamat</Typography>
            <Typography variant="body2" color={summary.address ? 'text.primary' : 'text.secondary'}>
              {summary.address || 'Alamat belum tersedia.'}
            </Typography>
          </Stack>

          {summary.notes ? (
            <Stack spacing={0.5}>
              <Typography variant="subtitle2">Catatan</Typography>
              <Typography variant="body2">{summary.notes}</Typography>
            </Stack>
          ) : null}

          {summary.transportasiNote?.trim() ? (
            <Stack spacing={0.5}>
              <Typography variant="subtitle2">Transportasi / kendaraan</Typography>
              <Typography variant="body2">{summary.transportasiNote.trim()}</Typography>
            </Stack>
          ) : null}

          <Stack spacing={0.5}>
            <Typography variant="subtitle2">Tahapan</Typography>
            <Typography
              variant="body2"
              color={tahapanCaption ? 'text.primary' : 'text.secondary'}
            >
              {tahapanCaption ?? '—'}
            </Typography>
          </Stack>

          {!canCreateWorkOrder && actionLabel && onAction && actionDisabled && actionDisabledHint ? (
            <Typography variant="body2" color="text.secondary">
              {actionDisabledHint}
            </Typography>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Button onClick={onClose} data-testid="schedule-quotation-day-detail-close">
          Tutup
        </Button>
        {onOpenSchedule ? (
          <Button
            variant="contained"
            onClick={onOpenSchedule}
            data-testid="schedule-open-selected"
          >
            Buka jadwal
          </Button>
        ) : null}
        {canEditSchedule && onEditSchedule ? (
          <Button variant="outlined" onClick={onEditSchedule} data-testid="schedule-edit-from-detail">
            Ubah jadwal
          </Button>
        ) : null}
        {canCreateWorkOrder && onCreateWorkOrder ? (
          <Button
            variant="contained"
            color="primary"
            onClick={onCreateWorkOrder}
            data-testid="schedule-create-work-order"
          >
            Buat work order
          </Button>
        ) : null}
        {!canCreateWorkOrder && actionLabel && onAction ? (
          <Button
            variant="contained"
            color="primary"
            disabled={actionDisabled}
            onClick={onAction}
            data-testid={actionTestId}
          >
            {actionLabel}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}
